import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  ShieldCheck, Bell, ArrowsClockwise, Trash, Check, ArrowSquareOut,
  Warning, Info, MagnifyingGlass, Database, Lightbulb, ListChecks,
  FileText, Sparkle,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const IMPACT_COLORS = {
  alto: 'bg-red-100 text-red-700 border-red-200',
  medio: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  basso: 'bg-slate-100 text-slate-600 border-slate-200',
};
const IMPACT_ICONS = { alto: Warning, medio: Bell, basso: Info };

export default function CompliancePage() {
  const [data, setData] = useState({ alerts: [], stats: {} });
  const [sources, setSources] = useState({ sources: [], configured: [] });
  const [suggestions, setSuggestions] = useState({ suggestions: [], gap_analysis: [], note: '' });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [filterImpact, setFilterImpact] = useState('all');
  const [filterUnread, setFilterUnread] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('alerts');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterUnread) params.set('unread_only', 'true');
      if (filterImpact !== 'all') params.set('impact', filterImpact);
      const [aRes, sRes] = await Promise.all([
        fetch(`${API}/api/compliance/alerts?${params}`, { credentials: 'include' }),
        fetch(`${API}/api/compliance/sources`, { credentials: 'include' }),
      ]);
      if (aRes.ok) setData(await aRes.json());
      if (sRes.ok) setSources(await sRes.json());
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [filterImpact, filterUnread]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const triggerScan = async () => {
    setScanning(true);
    toast.info('Scansione ARERA in corso... può richiedere 30-90 secondi');
    try {
      const res = await fetch(`${API}/api/compliance/scan-now`, {
        method: 'POST', credentials: 'include',
      });
      const result = await res.json();
      if (res.ok) {
        const skipped = result.skipped_duplicates ? ` (${result.skipped_duplicates} duplicati saltati)` : '';
        toast.success(`Scansione: ${result.new_alerts} nuove segnalazioni${skipped}`,
                      { duration: 8000 });
        fetchData();
      } else toast.error(result.detail || 'Errore scansione');
    } catch (e) { toast.error('Errore scansione (timeout?)'); }
    finally { setScanning(false); }
  };

  const generateSuggestions = async () => {
    setGeneratingSuggestions(true);
    toast.info('Generazione suggerimenti AI in corso...');
    try {
      const res = await fetch(`${API}/api/compliance/suggestions`, { credentials: 'include' });
      const data = await res.json();
      setSuggestions(data);
      if ((data.suggestions || []).length === 0) {
        toast.info(data.note || 'Nessun suggerimento generato');
      } else {
        toast.success(`${data.suggestions.length} suggerimenti generati`);
      }
    } catch (e) { toast.error('Errore generazione suggestions'); }
    finally { setGeneratingSuggestions(false); }
  };

  const markRead = async (aid) => {
    await fetch(`${API}/api/compliance/alerts/${aid}/read`, {
      method: 'POST', credentials: 'include',
    });
    fetchData();
  };

  const generateReport = async (aid) => {
    toast.info('Generazione relazione in corso (~60s)... PDF, indicizzazione KB, email');
    try {
      const res = await fetch(`${API}/api/compliance/alerts/${aid}/generate-report`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json();
      if (res.ok) {
        const parts = [`PDF generato`];
        if (d.kb_ingested) parts.push(`KB +${d.kb_chunks} chunks`);
        if (d.email_status === 'sent') parts.push(`Email a ${d.email_sent_to.length} dest.`);
        toast.success(`Relazione pronta: ${parts.join(' · ')}`, { duration: 8000 });
      } else toast.error(d.detail || 'Errore generazione');
    } catch (e) { toast.error('Errore generazione relazione'); }
  };

  const markAllRead = async () => {
    if (!data.stats.unread) return;
    await fetch(`${API}/api/compliance/alerts/mark-all-read`, {
      method: 'POST', credentials: 'include',
    });
    toast.success('Tutte segnate come lette');
    fetchData();
  };

  const remove = async (aid) => {
    if (!window.confirm('Eliminare questo alert?')) return;
    await fetch(`${API}/api/compliance/alerts/${aid}`, {
      method: 'DELETE', credentials: 'include',
    });
    fetchData();
  };

  const filtered = data.alerts.filter(a =>
    !search ||
    a.title?.toLowerCase().includes(search.toLowerCase()) ||
    a.summary?.toLowerCase().includes(search.toLowerCase()) ||
    (a.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
  );
  // (filtered usage moved into AlertsPanel)
  void filtered;

  if (loading) return <div className="p-8 text-slate-500">Caricamento Compliance...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="compliance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ShieldCheck size={32} weight="duotone" className="text-orange-500" />
            Compliance & Normativa
          </h1>
          <p className="text-slate-500 mt-1">
            Monitoraggio automatico settimanale di testi integrati e delibere ARERA (Elettricità + Gas).
            Scansione: lunedì 06:00.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={generateSuggestions} disabled={generatingSuggestions}
                  variant="outline" className="border-blue-300 text-blue-700"
                  data-testid="generate-suggestions-btn">
            <Lightbulb size={16} className="mr-2" />
            {generatingSuggestions ? 'AI...' : 'Suggerimenti AI'}
          </Button>
          <Button onClick={triggerScan} disabled={scanning}
                  className="bg-orange-500 hover:bg-orange-600" data-testid="scan-now-btn">
            <ArrowsClockwise size={16} className={`mr-2 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scansione...' : 'Scansiona ora'}
          </Button>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-1 border-b mb-4">
        {[
          { id: 'alerts', label: 'Alert ARERA', icon: Bell },
          { id: 'suggestions', label: 'Suggerimenti AI', icon: Lightbulb },
        ].map(t => {
          const TabIcon = t.icon;
          const sel = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
                    className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 transition ${
                      sel ? 'border-orange-500 text-orange-600 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`tab-${t.id}`}>
              <TabIcon size={16} />
              {t.label}
              {t.id === 'suggestions' && suggestions.suggestions?.length > 0 && (
                <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full">
                  {suggestions.suggestions.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'suggestions' ? (
        <SuggestionsPanel data={suggestions} onGenerate={generateSuggestions}
                           generating={generatingSuggestions} />
      ) : (
        <AlertsPanel
          data={data}
          sources={sources}
          search={search}
          setSearch={setSearch}
          filterImpact={filterImpact}
          setFilterImpact={setFilterImpact}
          filterUnread={filterUnread}
          setFilterUnread={setFilterUnread}
          markRead={markRead}
          markAllRead={markAllRead}
          remove={remove}
        />
      )}
    </div>
  );
}

// ============================================================
// COMPONENTI INTERNI
// ============================================================

function AlertsPanel({ data, sources, search, setSearch, filterImpact, setFilterImpact,
                       filterUnread, setFilterUnread, markRead, markAllRead, remove }) {
  const filtered = data.alerts.filter(a =>
    !search ||
    a.title?.toLowerCase().includes(search.toLowerCase()) ||
    a.summary?.toLowerCase().includes(search.toLowerCase()) ||
    (a.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      {/* STATS */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard label="Totale alert" value={data.stats.total || 0} color="text-slate-700" />
        <StatCard label="Da leggere" value={data.stats.unread || 0} color="text-orange-600"
                  active={filterUnread} onClick={() => setFilterUnread(!filterUnread)} />
        <StatCard label="Impatto alto" value={data.stats.unread_high || 0} color="text-red-600"
                  active={filterImpact === 'alto'}
                  onClick={() => setFilterImpact(filterImpact === 'alto' ? 'all' : 'alto')} />
        <StatCard label="Sorgenti monitorate" value={sources.configured.length}
                  color="text-blue-600" />
      </div>

      {/* SOURCES STATUS */}
      <div className="bg-white border rounded-lg p-3 mb-4">
        <div className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-2">
          <Database size={14} /> Sorgenti monitorate
        </div>
        <div className="grid grid-cols-2 gap-2">
          {sources.configured.map(c => {
            const snap = sources.sources.find(s => s.source_id === c.id);
            return (
              <div key={c.id} className="flex items-center justify-between text-xs border rounded p-2">
                <div>
                  <div className="font-medium text-slate-700">{c.label}</div>
                  <a href={c.url} target="_blank" rel="noreferrer"
                     className="text-orange-600 hover:underline flex items-center gap-1">
                    Apri ARERA <ArrowSquareOut size={10} />
                  </a>
                </div>
                <div className="text-right">
                  <div className="text-slate-700 font-mono">{snap?.items_count ?? '—'}</div>
                  <div className="text-slate-400">{snap?.last_scan_at ? snap.last_scan_at.slice(0, 10) : 'mai'}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SEARCH + FILTERS */}
      <div className="bg-white border rounded-lg p-3 mb-4 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-slate-400 ml-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Cerca per titolo, riassunto o tag..."
               className="border-0 focus-visible:ring-0" data-testid="alerts-search" />
        <div className="flex gap-1 mr-2">
          {['all', 'alto', 'medio', 'basso'].map(im => (
            <Button key={im} size="sm" variant={filterImpact === im ? 'default' : 'outline'}
                    onClick={() => setFilterImpact(im)}
                    className={filterImpact === im && im === 'alto' ? 'bg-red-500 hover:bg-red-600' : ''}
                    data-testid={`filter-impact-${im}`}>
              {im === 'all' ? 'Tutti' : im}
            </Button>
          ))}
        </div>
        {data.stats.unread > 0 && (
          <Button size="sm" variant="outline" onClick={markAllRead} data-testid="mark-all-read">
            <Check size={14} className="mr-1" /> Segna tutto letto
          </Button>
        )}
      </div>

      {/* LIST */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <ShieldCheck size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
          <p className="text-slate-500">
            {data.alerts.length === 0
              ? 'Nessun alert ancora. La prima scansione registra solo lo snapshot baseline; le novità appariranno alla scansione successiva.'
              : 'Nessun risultato per i filtri selezionati'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(a => {
            const ImpIcon = IMPACT_ICONS[a.impact] || Info;
            return (
              <div key={a.id} className={`bg-white rounded-lg border p-3 transition ${
                                          a.read ? 'opacity-60' : 'border-orange-200 shadow-sm'
                                        }`}
                   data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${IMPACT_COLORS[a.impact] || IMPACT_COLORS.basso}`}>
                    <ImpIcon size={12} weight="duotone" />
                    {a.impact?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <a href={a.url} target="_blank" rel="noreferrer"
                         className="font-semibold text-slate-900 hover:text-orange-600">
                        {a.title}
                      </a>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{a.source_label}</Badge>
                    </div>
                    {a.summary && a.summary !== a.title && (
                      <p className="text-sm text-slate-600 mt-1">{a.summary}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {a.delibera_id && (
                        <span className="text-[10px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded font-mono">
                          {a.delibera_id}
                        </span>
                      )}
                      {(a.tags || []).map((t, i) => (
                        <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                          #{t}
                        </span>
                      ))}
                      <span className="text-[10px] text-slate-400 ml-auto">
                        {(a.created_at || '').slice(0, 10)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => generateReport(a.id)}
                            title="Genera relazione PDF + indicizza in KB + invia email"
                            data-testid={`generate-report-${a.id}`}
                            className="text-orange-600 hover:bg-orange-50">
                      <Sparkle size={14} weight="duotone" />
                    </Button>
                    {!a.read && (
                      <Button size="sm" variant="ghost" onClick={() => markRead(a.id)}
                              title="Segna letto" data-testid={`mark-read-${a.id}`}>
                        <Check size={14} className="text-green-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(a.id)} title="Elimina">
                      <Trash size={14} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const PRIORITA_COLORS = {
  alta: 'bg-red-100 text-red-700 border-red-200',
  media: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  bassa: 'bg-slate-100 text-slate-600 border-slate-200',
};

function SuggestionsPanel({ data, onGenerate, generating }) {
  const sugs = data.suggestions || [];
  const gaps = data.gap_analysis || [];

  if (sugs.length === 0 && gaps.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center" data-testid="empty-suggestions">
        <Lightbulb size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
        <p className="text-slate-700 font-semibold">Suggerimenti AI non ancora generati</p>
        <p className="text-slate-500 text-sm mt-1">{data.note || 'Clicca "Suggerimenti AI" per analizzare gli alert recenti e ricevere consigli implementativi.'}</p>
        <Button onClick={onGenerate} disabled={generating}
                className="mt-4 bg-blue-600 hover:bg-blue-700">
          <Lightbulb size={14} className="mr-1" /> {generating ? 'Generazione...' : 'Genera suggerimenti'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suggestions-panel">
      <div className="text-xs text-slate-400">
        Generato da {data.alerts_analyzed} alert · {(data.generated_at || '').slice(0, 19).replace('T', ' ')}
      </div>

      {sugs.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Lightbulb size={20} weight="duotone" className="text-yellow-500" />
            Implementazioni suggerite ({sugs.length})
          </h2>
          <div className="space-y-2">
            {sugs.map((s, i) => (
              <div key={i} className="bg-white border rounded-lg p-4" data-testid={`suggestion-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`px-2 py-1 rounded text-xs font-bold border ${PRIORITA_COLORS[s.priorita?.toLowerCase()] || PRIORITA_COLORS.bassa}`}>
                    {s.priorita?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{s.titolo}</h3>
                    <p className="text-sm text-slate-600 mt-1">{s.descrizione}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {s.area && <Badge variant="outline" className="text-[10px]">{s.area}</Badge>}
                      {s.riferimento_delibera && (
                        <span className="text-[10px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded font-mono">
                          Rif: {s.riferimento_delibera}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {gaps.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <ListChecks size={20} weight="duotone" className="text-red-500" />
            Gap analysis — cosa manca ({gaps.length})
          </h2>
          <div className="space-y-2">
            {gaps.map((g, i) => (
              <div key={i} className="bg-white border-l-4 border-red-300 p-3 rounded">
                <div className="flex items-start gap-2">
                  <Warning size={16} weight="duotone" className="text-red-500 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">{g.area}</div>
                    <p className="text-sm text-slate-600 mt-1">{g.cosa_manca}</p>
                    {g.impatto && <p className="text-xs text-slate-400 mt-1">Impatto: {g.impatto}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, active, onClick }) {
  return (
    <div onClick={onClick}
         className={`bg-white border rounded-lg p-3 ${onClick ? 'cursor-pointer transition' : ''} ${
           active ? 'ring-2 ring-orange-500' : onClick ? 'hover:bg-slate-50' : ''
         }`}
         data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
